// resolveDrawMode.js — Derives the correct OL draw mode from MSInfo.
//
// Decision is based on DrawRule (the authoritative source), NOT getGeometry().
// getGeometry() returns "Area" for CORRIDOR/CIRCULAR/ELLIPSE/RECTANGULAR which
// would all land in draw-polygon incorrectly.
//
// Returned shape:
//   { mode: 'draw-point'|'draw-line'|'draw-polygon'|'draw-circle'|null,
//     minPoints: number, maxPoints: number,
//     fixed: boolean,
//     drawRule: number }   // raw DrawRule value; passed through to setMode opts

import { DrawRules } from '@armyc2.c5isr.renderer/mil-sym-ts-web';

const UNLIMITED = Number.MAX_VALUE;

// DrawRule numeric ranges (from live library enumeration)
const DR = {
  isArea:        (dr) => dr >= 101 && dr <= 127,
  isPoint:       (dr) => dr >= 201 && dr <= 218,
  // LINE50 (=350) is an out-of-range placeholder that still behaves as a 3-pt line rule.
  isLine:        (dr) => (dr >= 301 && dr <= 333) || dr === DrawRules.LINE50,
  isCorridor:    (dr) => dr >= 401 && dr <= 499,
  isAxis:        (dr) => dr >= 501 && dr <= 502,
  isPolyline:    (dr) => dr >= 601 && dr <= 699,
  isEllipse:     (dr) => dr >= 701 && dr <= 799,
  isRectangular: (dr) => dr >= 801 && dr <= 803,
  isCircular:    (dr) => dr >= 901 && dr <= 902,
  isArc:         (dr) => dr === 1001,
};

/**
 * @param {import('@armyc2.c5isr.renderer/mil-sym-ts-web').MSInfo} msInfo
 * @returns {{ mode: string|null, minPoints: number, maxPoints: number, fixed: boolean }}
 */
export function resolveDrawMode(msInfo) {
  if (!msInfo) return { mode: null, minPoints: 0, maxPoints: 0, fixed: false, drawRule: 0 };

  const dr  = msInfo.getDrawRule();
  const min = msInfo.getMinPointCount();
  const max = msInfo.getMaxPointCount();

  // Nothing to draw (category node, not a real graphic)
  if (dr === DrawRules.DONOTDRAW || dr === 0) {
    return { mode: null, minPoints: 0, maxPoints: 0, fixed: false, drawRule: dr };
  }

  // CIRCULAR: 2-click placement. center stored as Point + AM=radius (metres).
  if (DR.isCircular(dr)) {
    return { mode: 'draw-circle', minPoints: 2, maxPoints: 2, fixed: true, drawRule: dr };
  }

  // ELLIPSE / ARC: 1-click, dimensions set via properties panel.
  if (DR.isEllipse(dr) || DR.isArc(dr)) {
    return { mode: 'draw-point', minPoints: 1, maxPoints: 1, fixed: true, drawRule: dr };
  }

  // RECTANGULAR: 1-pt variant → single click; 2-pt variant → 2-click line.
  if (DR.isRectangular(dr)) {
    if (min === 1) return { mode: 'draw-point', minPoints: 1, maxPoints: 1, fixed: true, drawRule: dr };
    return { mode: 'draw-line', minPoints: 2, maxPoints: 2, fixed: true, drawRule: dr };
  }

  // CORRIDOR: spine is a polyline even though getGeometry() says "Area".
  if (DR.isCorridor(dr)) {
    return { mode: 'draw-line', minPoints: min, maxPoints: max, fixed: false, drawRule: dr };
  }

  // LINE / AXIS / POLYLINE families: open polyline.
  if (DR.isLine(dr) || DR.isAxis(dr) || DR.isPolyline(dr)) {
    const fixed = min > 0 && min === max;
    return { mode: 'draw-line', minPoints: min, maxPoints: max, fixed, drawRule: dr };
  }

  // AREA: closed polygon.
  // Special-case AREA22: single-point static symbol (P1: center only).
  // It falls inside the AREA numeric range (122) but is semantically a point placement.
  if (dr === DrawRules.AREA22) {
    return { mode: 'draw-point', minPoints: 1, maxPoints: 1, fixed: true, drawRule: dr };
  }
  if (DR.isArea(dr)) {
    const fixed = min > 0 && min === max;
    return { mode: 'draw-polygon', minPoints: min, maxPoints: max, fixed, drawRule: dr };
  }

  // POINT family: may have fixed multi-point forms (e.g. POINT11 = 3 pts).
  if (DR.isPoint(dr)) {
    if (min <= 1 && (max <= 1 || max === UNLIMITED)) {
      return { mode: 'draw-point', minPoints: 1, maxPoints: 1, fixed: true, drawRule: dr };
    }
    // Fixed multi-point (e.g. min=max=3): collect N clicks as a line, auto-finish.
    if (min === max) {
      return { mode: 'draw-line', minPoints: min, maxPoints: max, fixed: true, drawRule: dr };
    }
    // Variable multi-point under POINT family: treat as open line.
    return { mode: 'draw-line', minPoints: min, maxPoints: max, fixed: false, drawRule: dr };
  }

  // Unknown draw rule — fall back to single click.
  return { mode: 'draw-point', minPoints: 1, maxPoints: 1, fixed: true, drawRule: dr };
}

/**
 * Returns true for draw rules that place a single anchor point but whose rendered
 * shape is driven by AM/AN modifiers (set after placement via properties panel).
 * These must be committed as `tactical-point` and rendered via WebRenderer.
 *
 * Covered families:
 *   ELLIPSE   (701–799)  — semi-major/minor via AM
 *   RECTANGULAR2 (802)   — AM = length,width; AN = attitude
 *   RECTANGULAR3 (803)   — AM = width
 *   ARC       (1001)     — AN = azimuth; AM = range
 *   POINT17   (217)      — rectangle: AM = length,width; AN = rotation
 *   POINT18   (218)      — search-area fan: AN = azimuth; AM = start/stop range
 *
 * RECTANGULAR1 (801) uses 2 points so it never reaches draw-point routing.
 * CIRCULAR (901–902) uses draw-circle, not draw-point.
 *
 * @param {number} drawRule
 * @returns {boolean}
 */
export function isParametricPointDrawRule(drawRule) {
  // ELLIPSE family
  if (drawRule >= 701 && drawRule <= 799) return true;
  // RECTANGULAR single-point variants (RECTANGULAR2=802, RECTANGULAR3=803)
  if (drawRule === 802 || drawRule === 803) return true;
  // ARC family
  if (drawRule === 1001) return true;
  // POINT17 (rectangle) and POINT18 (search-area fan)
  if (drawRule === DrawRules.POINT17 || drawRule === DrawRules.POINT18) return true;
  return false;
}
